import {
  ACCESSORIES,
  ACCESSORY_KEYS,
  CONDITION_KEYS,
  ITEM_CONDITIONS,
  type ItemCondition,
} from "@kovernow/flows/add_item";
import { Checkbox } from "@kovernow/react-ui/components/checkbox";
import { Label } from "@kovernow/react-ui/components/label";
import { RadioGroup, RadioGroupItem } from "@kovernow/react-ui/components/radio-group";
import { useT } from "../../core/use-t";
import { STEP_HEADING_ID, StepActions, StepHeading, type StepProps } from "./shared";

/** Step 4: one of five conditions (default Brand new) and optional accessories. */
export function ConditionStep({
  fields,
  setFields,
  goTo,
  back,
  headingRef,
  serverErrors,
  clearServerError,
  onContinue,
  continueLabel,
}: StepProps & {
  /** Defaults to the photos step. */
  onContinue?: () => void;
  continueLabel?: string;
}) {
  const t = useT();
  const serverError = serverErrors.condition;
  return (
    <section aria-labelledby={STEP_HEADING_ID} className="flex flex-col gap-5" data-testid="step-condition">
      <StepHeading headingRef={headingRef}>{t("addItem.condition.title")}</StepHeading>
      <div className="flex flex-col gap-2">
        <p id="condition-label" className="font-semibold">
          {t("addItem.condition.label")}
        </p>
        <RadioGroup
          aria-labelledby="condition-label"
          aria-describedby={serverError ? "condition-error" : undefined}
          value={fields.condition}
          onValueChange={(v) => {
            setFields((f) => ({ ...f, condition: v as ItemCondition }));
            clearServerError("condition");
          }}
        >
          {ITEM_CONDITIONS.map((c, i) => (
            <div key={c} className="flex min-h-11 items-center gap-2">
              <RadioGroupItem value={c} id={`condition-${i}`} />
              <Label htmlFor={`condition-${i}`}>{t(CONDITION_KEYS[c])}</Label>
            </div>
          ))}
        </RadioGroup>
        {serverError ? (
          <p id="condition-error" className="text-sm font-semibold text-status-error">
            {t(serverError)}
          </p>
        ) : null}
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 font-semibold">{t("addItem.condition.accessories")}</legend>
        {ACCESSORIES.map((a, i) => (
          <div key={a} className="flex min-h-11 items-center gap-2">
            <Checkbox
              id={`accessory-${i}`}
              checked={fields.accessories.includes(a)}
              onCheckedChange={(on) =>
                setFields((f) => ({
                  ...f,
                  accessories: on === true ? [...f.accessories.filter((x) => x !== a), a] : f.accessories.filter((x) => x !== a),
                }))
              }
            />
            <Label htmlFor={`accessory-${i}`}>{t(ACCESSORY_KEYS[a])}</Label>
          </div>
        ))}
      </fieldset>
      <StepActions onBack={back} onContinue={onContinue ?? (() => goTo("photos"))} continueLabel={continueLabel} />
    </section>
  );
}
